/**
 * Owner: Masa Bayu
 * File: demo-library-extended.ts
 * Date:Time: 2026-10-18 19:20
 * Description: T4 library store §377 (bahagian 3/3) — query/surface/resume/
 *   version/archive/sync/entitlement-peek + applyExternalHubGrants (Fasa 3) +
 *   reset. Dipindah verbatim dari demo-library-store.ts; kelakuan sama.
 */

import {
  applyListedVersionReference,
  archiveLibraryItem,
  createLibraryResumeIntent,
  createLibrarySyncContract,
  createQxreadEntitlement,
  evaluateLibraryVersionAwareness,
  queryLibraryItems,
  resolveLibrarySurfaceView,
  type QxreadEntitlement,
  type QxreadLibraryFilterKey,
  type QxreadLibraryItem,
  type QxreadLibraryReaderLaunchIntent,
  type QxreadLibraryResumeIntent,
  type QxreadLibrarySortKey,
  type QxreadLibrarySurfaceView,
  type QxreadLibrarySyncContract,
  type QxreadLibraryVersionAwareness,
  type QxreadTelemetryEvent,
} from '@qx/qxread-core';
import {
  DEMO_ACCOUNT_ID,
  authorsMap,
  bumpDemoLibraryRevision,
  demoLibraryState,
  getDemoLibraryProjection,
  record,
} from './demo-library-base';

export function setDemoLibraryQuery(options: {
  readonly filter?: QxreadLibraryFilterKey;
  readonly sort?: QxreadLibrarySortKey;
  readonly query?: string;
}): readonly QxreadLibraryItem[] {
  if (options.filter !== undefined) {
    demoLibraryState.queryFilter = options.filter;
  }
  if (options.sort !== undefined) {
    demoLibraryState.querySort = options.sort;
  }
  if (options.query !== undefined) {
    demoLibraryState.queryText = options.query;
  }
  bumpDemoLibraryRevision();
  return getDemoQueriedLibraryItems();
}

export function getDemoQueriedLibraryItems(): readonly QxreadLibraryItem[] {
  const projection = getDemoLibraryProjection();
  const pool = [...projection.purchased, ...projection.blocked, ...projection.archived];
  const results = queryLibraryItems(pool, {
    filter: demoLibraryState.queryFilter,
    sort: demoLibraryState.querySort,
    query: demoLibraryState.queryText,
    authorsByPublicationId: authorsMap(),
  });
  demoLibraryState.lastSurface = resolveLibrarySurfaceView({ items: results });
  record('qxread.library.query', {
    filter: demoLibraryState.queryFilter,
    sort: demoLibraryState.querySort,
    query: demoLibraryState.queryText,
    count: results.length,
    surface: demoLibraryState.lastSurface.state,
  });
  return results;
}

export function getDemoLibrarySurface(): QxreadLibrarySurfaceView {
  if (demoLibraryState.lastSurface === null) {
    const projection = getDemoLibraryProjection();
    demoLibraryState.lastSurface = resolveLibrarySurfaceView({ items: projection.purchased });
  }
  return demoLibraryState.lastSurface;
}

export function requestDemoResume(publicationId: string): QxreadLibraryResumeIntent {
  getDemoLibraryProjection();
  const entitlement = demoLibraryState.entitlements.find(
    (item) => item.publicationId === publicationId,
  );
  if (entitlement === undefined) {
    throw new Error('Demo resume entitlement not found');
  }
  const item = demoLibraryState.itemsByEntitlementId.get(entitlement.entitlementId);
  if (item === undefined) {
    throw new Error('Demo resume library item not found');
  }
  demoLibraryState.lastResumeIntent = createLibraryResumeIntent({
    item,
    entitlement,
    sessionAuthenticated: true,
    accountId: DEMO_ACCOUNT_ID,
  });
  record('qxread.library.resume_intent', {
    publicationId,
    allowed: demoLibraryState.lastResumeIntent.allowed,
    executesReader: false,
    lastPositionRef: demoLibraryState.lastResumeIntent.lastPositionRef,
  });
  return demoLibraryState.lastResumeIntent;
}

export function simulateDemoVersionUpdate(publicationId: string): {
  readonly item: QxreadLibraryItem;
  readonly awareness: QxreadLibraryVersionAwareness;
} {
  getDemoLibraryProjection();
  const entitlement = demoLibraryState.entitlements.find(
    (item) => item.publicationId === publicationId,
  );
  if (entitlement === undefined) {
    throw new Error('Demo version entitlement not found');
  }
  const item = demoLibraryState.itemsByEntitlementId.get(entitlement.entitlementId);
  if (item === undefined) {
    throw new Error('Demo version library item not found');
  }
  const current = item.listedVersionLabel ?? '1.0.0';
  const nextLabel = `${current.split('.')[0] ?? '1'}.${String(Number(current.split('.')[1] ?? '0') + 1)}.0`;
  const updated = applyListedVersionReference(item, nextLabel);
  demoLibraryState.itemsByEntitlementId.set(entitlement.entitlementId, updated);
  const awareness = evaluateLibraryVersionAwareness(updated);
  record('qxread.library.version_awareness', {
    publicationId,
    updateAvailable: awareness.updateAvailable,
    mutatesQxp: false,
  });
  return Object.freeze({ item: updated, awareness });
}

export function archiveDemoLibraryItem(publicationId: string): QxreadLibraryItem {
  getDemoLibraryProjection();
  const entitlement = demoLibraryState.entitlements.find(
    (item) => item.publicationId === publicationId,
  );
  if (entitlement === undefined) {
    throw new Error('Demo archive entitlement not found');
  }
  const item = demoLibraryState.itemsByEntitlementId.get(entitlement.entitlementId);
  if (item === undefined) {
    throw new Error('Demo archive library item not found');
  }
  const archived = archiveLibraryItem(item);
  demoLibraryState.itemsByEntitlementId.set(entitlement.entitlementId, archived);
  return archived;
}

export function getDemoResumeIntent(): QxreadLibraryResumeIntent | null {
  return demoLibraryState.lastResumeIntent;
}

export function getDemoLibraryQueryState(): {
  readonly filter: QxreadLibraryFilterKey;
  readonly sort: QxreadLibrarySortKey;
  readonly query: string;
} {
  return Object.freeze({
    filter: demoLibraryState.queryFilter,
    sort: demoLibraryState.querySort,
    query: demoLibraryState.queryText,
  });
}

export function getDemoLibrarySync(): QxreadLibrarySyncContract {
  return demoLibraryState.sync;
}

export function setDemoLibrarySync(next: QxreadLibrarySyncContract): void {
  demoLibraryState.sync = next;
}

export function applyDemoLibraryItemUpdate(item: QxreadLibraryItem): void {
  demoLibraryState.itemsByEntitlementId.set(item.entitlementId, item);
}

export function peekDemoEntitlements(): readonly QxreadEntitlement[] {
  return demoLibraryState.entitlements;
}

export function writeDemoEntitlements(next: readonly QxreadEntitlement[]): void {
  demoLibraryState.entitlements = [...next];
}

/**
 * §509 F3 — publicationId yang pernah lahir daripada grant hub. Hanya entri dalam set
 * ini yang menjadi milik hub dan boleh diturunkan/dibuang olehnya; entitlement
 * setempat (DEV lab / aliran demo) tidak diusik.
 */
const hubSourcedPublicationIds = new Set<string>();
export interface HubGrantApplyOptions {
  /**
   * `true` apabila senarai ialah SNAPSHOT PENUH akaun (jawapan 200
   * `GET /v1/entitlements`). Grant hub yang tiada dalam snapshot = ditarik balik
   * oleh pelayan → turun ke `revoked`. Laluan berperingkat (satu grant selepas
   * fulfillment) mesti TIDAK menggunakan ini — satu grant bukan senarai penuh.
   */
  readonly fullSnapshot?: boolean;
}

/**
 * Fasa 3 — gabung grant qxp-hub (SSOT server) ke dalam proyeksi peranti.
 * Server menang pada pertindihan publicationId: keadaan/scope server menggantikan
 * setempat, entitlementId setempat dikekalkan untuk kesinambungan item library;
 * grant baharu (mengikut grantId) ditambah.
 * §509 F3 — `fullSnapshot` membetulkan bacaan "device cache ⊃ server truth": pada
 * snapshot, penghilangan ialah penarikan balik, bukan ketiadaan data.
 */
export function applyExternalHubGrants(
  grants: readonly {
    readonly grantId: string;
    readonly publicationId: string;
    readonly scope: string;
    readonly state: string;
  }[],
  options: HubGrantApplyOptions = {},
): number {
  const scopeOk = (value: string): value is QxreadEntitlement['scope'] =>
    (['preview', 'owned', 'subscription', 'licensed', 'author', 'admin'] as const).some(
      (scope) => scope === value,
    );
  const stateOk = (value: string): value is QxreadEntitlement['state'] =>
    (['pending_grant', 'active', 'grace', 'expired', 'revoked', 'grant_failed'] as const).some(
      (state) => state === value,
    );

  let applied = 0;
  const merged = [...demoLibraryState.entitlements];
  for (const grant of grants) {
    if (!scopeOk(grant.scope) || !stateOk(grant.state)) continue;
    hubSourcedPublicationIds.add(grant.publicationId);
    const idx = merged.findIndex((entry) => entry.publicationId === grant.publicationId);
    if (idx >= 0) {
      const existing = merged[idx];
      if (existing === undefined) continue;
      if (existing.scope === grant.scope && existing.state === grant.state) {
        continue;
      }
      merged[idx] = Object.freeze({ ...existing, scope: grant.scope, state: grant.state });
      applied += 1;
      continue;
    }
    merged.push(
      createQxreadEntitlement({
        accountId: DEMO_ACCOUNT_ID,
        publicationId: grant.publicationId,
        entitlementId: grant.grantId,
        scope: grant.scope,
        state: grant.state,
      }),
    );
    applied += 1;
  }

  if (options.fullSnapshot === true) {
    // Snapshot 200 yang sah: apa yang pelayan dah tak senaraikan sudah bukan hak kita.
    const listed = new Set(grants.map((grant) => grant.publicationId));
    for (let i = 0; i < merged.length; i += 1) {
      const entry = merged[i];
      if (entry === undefined) continue;
      if (!hubSourcedPublicationIds.has(entry.publicationId)) continue;
      if (listed.has(entry.publicationId)) continue;
      if (entry.state === 'revoked') continue;
      merged[i] = Object.freeze({ ...entry, state: 'revoked' });
      applied += 1;
    }
  }

  if (applied > 0) {
    demoLibraryState.entitlements = merged;
    // §458b — grant tiba lewat (sync tak segerak); permukaan yang sudah mount
    // mesti dilukis semula, jika tidak rak kekal kosong walaupun pelayan punya hak.
    bumpDemoLibraryRevision();
    record('qxread.library.sync_state', {
      externalGrants: grants.length,
      applied,
      fullSnapshot: options.fullSnapshot === true,
    });
  }
  return applied;
}

/**
 * §509 F4 — sesi berakhir (log keluar / 401). Hak yang lahir daripada grant hub bukan
 * lagi milik sesi ini → dibuang daripada proyeksi setempat; artefak dalam ingatan
 * dibuang oleh pemanggil (hub-sync). Entitlement setempat dibiarkan — ia tidak pernah
 * memberi akses ke muatan CDN yang digate pelayan.
 */
export function dropHubSourcedEntitlements(): number {
  if (hubSourcedPublicationIds.size === 0) return 0;
  const ownedByHub = new Set(hubSourcedPublicationIds);
  hubSourcedPublicationIds.clear();
  const before = demoLibraryState.entitlements.length;
  demoLibraryState.entitlements = demoLibraryState.entitlements.filter(
    (entry) => !ownedByHub.has(entry.publicationId),
  );
  const dropped = before - demoLibraryState.entitlements.length;
  if (dropped > 0) {
    bumpDemoLibraryRevision();
    record('qxread.library.sync_state', { sessionEnded: true, dropped });
  }
  return dropped;
}

export function getDemoLaunchIntent(): QxreadLibraryReaderLaunchIntent | null {
  return demoLibraryState.lastLaunchIntent;
}

export function getDemoLibraryTelemetry(): QxreadTelemetryEvent | null {
  return demoLibraryState.lastTelemetry;
}

export function resetDemoLibraryStore(): void {
  hubSourcedPublicationIds.clear();
  demoLibraryState.entitlements = [];
  demoLibraryState.itemsByEntitlementId = new Map();
  demoLibraryState.wishlist = [];
  demoLibraryState.sync = createLibrarySyncContract({ accountId: DEMO_ACCOUNT_ID });
  demoLibraryState.lastLaunchIntent = null;
  demoLibraryState.lastResumeIntent = null;
  demoLibraryState.lastTelemetry = null;
  demoLibraryState.lastSurface = null;
  demoLibraryState.queryFilter = 'all';
  demoLibraryState.querySort = 'recent';
  demoLibraryState.queryText = '';
}
